import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Truck, Trash2, Mail, Phone, MapPin } from 'lucide-react';

export default function SupplierTab() {
  const [suppliers, setSuppliers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', email: '', phone: '', address: '', items_supplied: [] });
  const [newItem, setNewItem] = useState('');

  const fetchSuppliers = useCallback(async () => {
    try { const { data } = await api.get('/suppliers'); setSuppliers(data); } catch { /* */ }
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const resetForm = () => { setForm({ name: '', contact_person: '', email: '', phone: '', address: '', items_supplied: [] }); setEditSupplier(null); };
  const openNew = () => { resetForm(); setFormOpen(true); };
  const openEdit = (s) => { setForm({ ...s }); setEditSupplier(s); setFormOpen(true); };

  const addSuppliedItem = () => {
    if (!newItem.trim()) return;
    setForm(p => ({ ...p, items_supplied: [...p.items_supplied, newItem.trim()] }));
    setNewItem('');
  };

  const removeSuppliedItem = (idx) => {
    setForm(p => ({ ...p, items_supplied: p.items_supplied.filter((_, i) => i !== idx) }));
  };

  const saveSupplier = async () => {
    try {
      if (editSupplier) { await api.put(`/suppliers/${editSupplier.id}`, form); toast.success('Supplier updated'); }
      else { await api.post('/suppliers', form); toast.success('Supplier created'); }
      setFormOpen(false); resetForm(); fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const deleteSupplier = async (id) => {
    try { await api.delete(`/suppliers/${id}`); toast.success('Deleted'); fetchSuppliers(); } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-sans text-zinc-400 uppercase tracking-wider">{suppliers.length} suppliers</h3>
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogTrigger asChild>
            <Button size="sm" onClick={openNew} className="bg-gold text-black rounded-sm font-sans text-xs font-bold" data-testid="add-supplier-button"><Plus className="w-3 h-3 mr-1" /> Add Supplier</Button>
          </DialogTrigger>
          <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-white">{editSupplier ? 'Edit' : 'New'} Supplier</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2 max-h-[60vh] overflow-y-auto">
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Company Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="supplier-name-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Contact Person</Label>
                <Input value={form.contact_person} onChange={e => setForm(p => ({ ...p, contact_person: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="supplier-contact-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="supplier-email-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Phone</Label>
                  <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="supplier-phone-input" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Address</Label>
                <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Items Supplied</Label>
                <div className="flex gap-2">
                  <Input value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSuppliedItem())} className="bg-transparent border border-white/10 text-white font-sans flex-1" placeholder="e.g. Rum, Vodka" data-testid="supplier-item-input" />
                  <Button type="button" size="sm" onClick={addSuppliedItem} className="bg-white/5 text-white rounded-sm" data-testid="add-supplied-item-button"><Plus className="w-3 h-3" /></Button>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {form.items_supplied.map((item, idx) => (
                    <span key={`form-${item}-${idx}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/5 border border-white/10 rounded-sm text-xs text-zinc-300 font-sans">
                      {item}
                      <button onClick={() => removeSuppliedItem(idx)} className="text-zinc-500 hover:text-red-400">&times;</button>
                    </span>
                  ))}
                </div>
              </div>
              <Button onClick={saveSupplier} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="save-supplier-button">{editSupplier ? 'Update' : 'Create'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Supplier List */}
      <div className="space-y-3">
        {suppliers.map(s => (
          <div key={s.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm card-hover" data-testid={`supplier-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Truck className="w-4 h-4 text-gold" />
                </div>
                <div>
                  <p className="text-white font-sans font-medium">{s.name}</p>
                  {s.contact_person && <p className="text-zinc-400 text-xs font-sans">{s.contact_person}</p>}
                  <div className="flex flex-wrap gap-3 mt-1.5">
                    {s.email && <span className="text-zinc-500 text-xs font-sans flex items-center gap-1"><Mail className="w-3 h-3" /> {s.email}</span>}
                    {s.phone && <span className="text-zinc-500 text-xs font-sans flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone}</span>}
                    {s.address && <span className="text-zinc-500 text-xs font-sans flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.address}</span>}
                  </div>
                  {s.items_supplied?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {s.items_supplied.map((item, idx) => (
                        <span key={`${s.id}-${item}-${idx}`} className="px-2 py-0.5 bg-gold/10 text-gold border border-gold/20 rounded-sm text-xs font-sans">{item}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="h-7 text-zinc-400 hover:text-white font-sans text-xs" data-testid={`edit-supplier-${s.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => deleteSupplier(s.id)} className="h-7 text-red-400 hover:text-red-300" data-testid={`delete-supplier-${s.id}`}><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        ))}
        {suppliers.length === 0 && <p className="text-zinc-500 font-sans text-center py-8" data-testid="no-suppliers">No suppliers yet. Add your first supplier above.</p>}
      </div>
    </div>
  );
}
